import React, { useState } from "react";
import { Link } from "react-router-dom";
import { SITE } from "./config";
import { api } from "@/lib/apiClient";
import Seo from "./Seo";
import {
  Phone,
  MapPin,
  ArrowRight,
  Scale,
  CheckCircle2,
  Truck,
  Recycle,
  Leaf,
  ShieldCheck,
  Building2,
  Sparkles,
  Zap,
} from "lucide-react";

const IMG_HERO = "https://images.unsplash.com/photo-1775157478624-4f9f718dcb3f?w=1600&q=80&auto=format&fit=crop";
const IMG_ABOUT = "https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?w=1200&q=80&auto=format&fit=crop";

export default function Home() {
  const [demoBusy, setDemoBusy] = useState(false);

  const startDemo = async () => {
    setDemoBusy(true);
    try {
      await api.post("/auth/demo-login");
      window.location.href = "/mon-espace";
    } catch {
      setDemoBusy(false);
    }
  };

  return (
    <div>
      <Seo
        title="FB Environnement — Centre de recyclage à Saint-Victoret (13)"
        description="Centre de recyclage agréé à Saint-Victoret, près de Marseille. Gravats, plâtre, déchets verts, bois, DIB — du tri à la valorisation. Lun-Ven 7h-17h non-stop."
        path="/"
      />
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMG_HERO} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0f3a18]/95 via-[#1F6B2E]/85 to-[#1F6B2E]/60" />
        </div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-20 sm:py-28">
          <span className="inline-block bg-[#9DCB2E] text-[#0f3a18] font-black uppercase tracking-[0.15em] text-xs px-3 py-1.5 rounded-full">
            Saint-Victoret · Bouches-du-Rhône
          </span>
          <h1 className="font-heading font-black text-white text-4xl sm:text-6xl tracking-tight mt-5 max-w-3xl leading-tight">
            Vos déchets professionnels,<br />
            <span className="text-[#9DCB2E]">on s&apos;en occupe.</span>
          </h1>
          <p className="text-stone-100 text-lg mt-5 max-w-2xl">
            Centre de recyclage agréé. Apportez vos déchets, on pèse, on trie, on valorise.
            Une solution simple, traçable et locale pour les artisans, le BTP et les entreprises.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/contact"
              data-testid="hero-cta-contact"
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur text-white border border-white/30 font-bold px-6 py-3.5 rounded-full transition-all active:scale-95"
            >
              Nous contacter
              <ArrowRight className="w-4 h-4" strokeWidth={3} />
            </Link>
            <a
              href={`tel:${SITE.phoneRaw}`}
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur text-white border border-white/30 font-bold px-6 py-3.5 rounded-full transition-all active:scale-95"
            >
              <Phone className="w-4 h-4" strokeWidth={2.8} /> {SITE.phone}
            </a>
          </div>
        </div>
      </section>

      {/* KPIs */}
      <section className="bg-white border-y border-stone-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
          <Kpi value="10" label="Matières acceptées" />
          <Kpi value="100%" label="Tracée & certifiée" />
          <Kpi value="5j/7" label="Ouvert" />
          <Kpi value="13730" label="Saint-Victoret" />
        </div>
      </section>

      {/* Comment ça marche (résumé) */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <span className="text-xs font-black text-[#1F6B2E] uppercase tracking-[0.2em]">3 étapes simples</span>
          <h2 className="font-heading font-black text-3xl sm:text-4xl mt-3 text-stone-900">
            Comment ça marche
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          <Step
            n="1"
            icon={<Truck className="w-7 h-7" strokeWidth={2.5} />}
            title="Vous apportez"
            text="Camion, fourgon, benne — vous déposez vos déchets sur notre site de Saint-Victoret."
          />
          <Step
            n="2"
            icon={<Scale className="w-7 h-7" strokeWidth={2.5} />}
            title="On pèse"
            text="Pesée précise sur notre pont-bascule. Bon de pesée remis à chaque passage."
          />
          <Step
            n="3"
            icon={<Recycle className="w-7 h-7" strokeWidth={2.5} />}
            title="On valorise"
            text="Tri, traitement et valorisation des matières dans le respect de la réglementation."
          />
        </div>
        <div className="mt-8 text-center">
          <Link
            to="/comment-ca-marche"
            className="inline-flex items-center gap-1 text-[#1F6B2E] hover:text-[#0f3a18] font-bold underline-offset-4 hover:underline"
          >
            Voir le détail <ArrowRight className="w-4 h-4" strokeWidth={3} />
          </Link>
        </div>
      </section>

      {/* Pourquoi nous */}
      <section className="bg-[#f6faf0]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="text-xs font-black text-[#1F6B2E] uppercase tracking-[0.2em]">Pourquoi FB Environnement</span>
            <h2 className="font-heading font-black text-3xl sm:text-4xl mt-3 text-stone-900">
              Un partenaire fiable et local pour vos déchets pro
            </h2>
            <ul className="mt-6 space-y-3">
              {[
                ["Tarification transparente", "Au choix : à la tonne ou à la benne, selon votre besoin."],
                ["Traçabilité totale", "Bon de pesée et bordereau de suivi remis à chaque dépôt."],
                ["Proximité Saint-Victoret", "Idéalement situé entre Marignane, Vitrolles et Marseille."],
                ["Accueil pro & rapide", "Une équipe à votre écoute, 6 jours sur 7."],
              ].map(([h, p]) => (
                <li key={h} className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-[#9DCB2E] text-[#0f3a18] grid place-items-center shrink-0 mt-0.5">
                    <CheckCircle2 className="w-4 h-4" strokeWidth={3} />
                  </span>
                  <div>
                    <div className="font-bold text-stone-900">{h}</div>
                    <div className="text-sm text-stone-600">{p}</div>
                  </div>
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/dechets"
                className="inline-flex items-center gap-2 bg-[#1F6B2E] hover:bg-[#175522] text-white font-bold px-5 py-3 rounded-full transition-all active:scale-95 shadow-md shadow-[#1F6B2E]/20"
              >
                Voir nos déchets <ArrowRight className="w-4 h-4" strokeWidth={3} />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-white hover:bg-stone-50 text-[#1F6B2E] border-2 border-[#1F6B2E] font-bold px-5 py-3 rounded-full transition-all active:scale-95"
              >
                Nous contacter
              </Link>
            </div>
          </div>
          <div className="relative">
            <div className="rounded-3xl overflow-hidden shadow-2xl shadow-[#1F6B2E]/20 aspect-[4/3]">
              <img src={IMG_ABOUT} alt="Centre de recyclage" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-5 -left-5 bg-white rounded-2xl shadow-xl p-4 flex items-center gap-3 max-w-xs">
              <div className="w-12 h-12 rounded-2xl bg-[#9DCB2E] grid place-items-center">
                <Leaf className="w-6 h-6 text-[#0f3a18]" strokeWidth={2.5} />
              </div>
              <div>
                <div className="font-heading font-black text-stone-900 leading-tight">100 % local</div>
                <div className="text-xs text-stone-500">Implantation Saint-Victoret</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Clients types */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <span className="text-xs font-black text-[#1F6B2E] uppercase tracking-[0.2em]">Pour qui ?</span>
          <h2 className="font-heading font-black text-3xl sm:text-4xl mt-3 text-stone-900">
            Une solution pour chaque professionnel
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: <Building2 className="w-6 h-6" strokeWidth={2.5} />, t: "Artisans BTP", d: "Maçons, plâtriers, menuisiers" },
            { icon: <Truck className="w-6 h-6" strokeWidth={2.5} />, t: "Entreprises de transport", d: "Évacuation rapide" },
            { icon: <ShieldCheck className="w-6 h-6" strokeWidth={2.5} />, t: "Industriels", d: "DIB, archives, métaux" },
            { icon: <Recycle className="w-6 h-6" strokeWidth={2.5} />, t: "Démolisseurs", d: "Gravats en gros volume" },
          ].map((c) => (
            <div key={c.t} className="bg-white border border-stone-100 rounded-2xl p-5 hover:-translate-y-1 hover:shadow-lg transition-all">
              <div className="w-12 h-12 rounded-2xl bg-[#9DCB2E]/20 text-[#1F6B2E] grid place-items-center mb-3">
                {c.icon}
              </div>
              <div className="font-heading font-black text-base">{c.t}</div>
              <div className="text-sm text-stone-500 mt-1">{c.d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA final */}
      <section className="bg-gradient-to-br from-[#1F6B2E] to-[#0f3a18] text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 text-center">
          <h2 className="font-heading font-black text-3xl sm:text-4xl">
            Une question, un déchet inhabituel, un gros volume ?
          </h2>
          <p className="text-stone-200 mt-3 max-w-2xl mx-auto">
            Notre équipe vous répond rapidement et trouve la meilleure solution.
          </p>
          <div className="mt-7 flex flex-wrap gap-3 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-[#9DCB2E] hover:bg-[#86b323] text-[#0f3a18] font-black px-6 py-3.5 rounded-full transition-all active:scale-95"
            >
              Nous contacter <ArrowRight className="w-4 h-4" strokeWidth={3} />
            </Link>
            <a
              href={`tel:${SITE.phoneRaw}`}
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/30 font-bold px-6 py-3.5 rounded-full transition-all active:scale-95"
            >
              <Phone className="w-4 h-4" strokeWidth={2.8} /> {SITE.phone}
            </a>
          </div>
          <p className="text-stone-200 text-sm mt-6 inline-flex items-center gap-2">
            <MapPin className="w-4 h-4" strokeWidth={2.5} /> {SITE.address.full}
          </p>
        </div>
      </section>
    </div>
  );
}

function Kpi({ value, label }) {
  return (
    <div className="text-center">
      <div className="font-heading font-black text-3xl sm:text-4xl text-[#1F6B2E]">{value}</div>
      <div className="text-xs text-stone-500 uppercase tracking-wider font-bold mt-1">{label}</div>
    </div>
  );
}

function Step({ n, icon, title, text }) {
  return (
    <div className="relative bg-white border border-stone-100 rounded-3xl p-6 hover:-translate-y-1 hover:shadow-lg transition-all">
      <div className="absolute -top-3 -left-3 w-10 h-10 rounded-full bg-[#9DCB2E] text-[#0f3a18] font-heading font-black grid place-items-center text-lg shadow-md">
        {n}
      </div>
      <div className="w-14 h-14 rounded-2xl bg-[#1F6B2E] text-white grid place-items-center mb-4">{icon}</div>
      <h3 className="font-heading font-black text-xl">{title}</h3>
      <p className="text-stone-600 text-sm mt-2">{text}</p>
    </div>
  );
}
